import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import CustomButton from "../../../components/CustomButton";
import { isValidEmail } from "../../../utils/helpers";
import httpRequest from "../../../ajax";
import "./index.scss";

const ENABLE_LEAD_MODAL = false;

const PostAJobPreview = () => {
  const history = useHistory();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadEmail, setLeadEmail] = useState("");
  const [leadEmailError, setLeadEmailError] = useState("");
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);

    const timer = setTimeout(() => {
      if (ENABLE_LEAD_MODAL && !sessionStorage.getItem("intent")) {
        setShowLeadModal(true);
      }
    }, 15000);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timer);
    };
  }, []);

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadEmailError("");
    setLeadSuccess(false);

    if (!isValidEmail(leadEmail)) {
      setLeadEmailError("Please enter a valid email address");
      return;
    }

    setLeadLoading(true);
    try {
      await httpRequest("post", "/waitlist", { email: leadEmail, intent: "post-job" });
      setLeadSuccess(true);
    } catch (err) {
      setLeadEmailError("Failed to submit. Please try again.");
    } finally {
      setLeadLoading(false);
    }
  };

  const handlePostAJobClick = () => {
    localStorage.setItem("accountType", "business");
    sessionStorage.setItem("intent", "post-job");
    history.push("/signup");
  };

  const handleSignInClick = () => {
    history.push("/signin");
  };

  return (
    <div className="post-job-preview">
      {/* 1. Nav Section */}
      <nav className={`preview-nav ${isScrolled ? "scrolled" : ""}`}>
        <div className="nav-container">
          <div className="nav-logo">workbrook</div>
          <div className="nav-actions">
            <button className="sign-in-btn" onClick={handleSignInClick}>
              Sign in
            </button>
            <CustomButton
              text="Post a Job"
              backgroundColor="#fff"
              color="#3A6ED4"
              borderColor="transparent"
              additionalClass="nav-cta-btn"
              handleClick={handlePostAJobClick}
            />
          </div>
        </div>
      </nav>

      {/* 2. Hero Section */}
      <section className="preview-hero">
        <div className="hero-content">
          <div className="hero-badge">FOR EMPLOYERS</div>
          <h1 className="hero-title">
            Hire faster.<br />
            Reach qualified<br />
            candidates.<br />
            Save hours every week.
          </h1>
          <p className="hero-subtitle">
            Post your open roles, and find candidates who already<br />
            match what you're looking for.
          </p>
          <div className="hero-tagline">workbrook • No work left undone</div>
          <CustomButton
            text="Post a Job"
            backgroundColor="#fff"
            color="#3A6ED4"
            borderColor="transparent"
            additionalClass="hero-cta-btn"
            handleClick={handlePostAJobClick}
          />
          <p className="hero-disclaimer">Free to sign up. No credit card required.</p>
        </div>
      </section>

      {/* 3. Tiers Section */}
      <section className="preview-tiers">
        <div className="tiers-header">
          <div className="section-badge">JOB TIERS</div>
          <h2>Choose the right tier for your role</h2>
          <p>Every job on Workbrook is posted under a tier that determines<br />visibility, reach, and referral eligibility.</p>
        </div>

        <div className="tiers-container">
          {/* Tier 1 */}
          <div className="tier-card">
            <div className="tier-name">TIER 1</div>
            <h3>Standard</h3>
            <p className="tier-for">Best for entry-level roles</p>
            <ul className="tier-features">
              <li><span>✓</span> Listed on Workbrook jobs feed</li>
              <li><span>✓</span> AI match score for applicants</li>
              <li><span>✓</span> Applicant management dashboard</li>
              <li><span>✓</span> Verified listing goes live instantly</li>
            </ul>
            <div className="tier-footer">
              <strong>Pay per listing</strong>
            </div>
          </div>

          {/* Tier 2 */}
          <div className="tier-card enhanced">
            <div className="tier-name">TIER 2</div>
            <h3>Enhanced</h3>
            <p className="tier-for">Best for mid-level roles</p>
            <ul className="tier-features">
              <li><span className="check-blue">✓</span> Everything in Tier 1</li>
              <li><span className="check-blue">✓</span> Priority placement in feed</li>
              <li><span className="check-blue">✓</span> Distributed to partner platforms</li>
              <li><span className="check-blue bold-check">✓</span> <strong>Candidate ranking and summary</strong></li>
              <li><span className="check-blue">✓</span> Pay per view — wider reach</li>
            </ul>
            <div className="tier-footer">
              <strong className="blue-text">Pay per view</strong>
            </div>
          </div>

          {/* Tier 3 */}
          <div className="tier-card premium">
            <div className="tier-badge">RECOMMENDED</div>
            <div className="tier-name">TIER 3</div>
            <h3>Referral-Powered</h3>
            <p className="tier-for">Best for executive, senior or urgent hires</p>
            <ul className="tier-features">
              <li><span className="check-white">✓</span> Everything in Tier 1 & 2</li>
              <li><span className="check-white">✓</span> Enters the referral reward pool</li>
              <li><span className="check-white">✓</span> Promoted to active referrers</li>
              <li><span className="check-white bold-check">✓</span> <strong>AI talent discovery</strong></li>
              <li><span className="check-white bold-check">✓</span> <strong>Candidate ranking and summary</strong></li>
              <li><span className="check-white">✓</span> 10% placement fee on successful hire</li>
            </ul>
            <div className="tier-footer">
              <strong className="blue-bg-text">10% placement fee on hire</strong>
            </div>
          </div>
        </div>

        <div className="tiers-cta">
          <CustomButton
            text="Post a Job"
            backgroundColor="#fff"
            color="#000"
            borderColor="#3A6ED4"
            additionalClass="outline-cta-btn"
            handleClick={handlePostAJobClick}
          />
        </div>
      </section>

      {/* 4. Steps Section */}
      <section className="preview-steps">
        <div className="steps-header">
          <div className="section-badge">HOW IT WORKS</div>
          <h2>Post a job in 4 simple steps</h2>
          <p>The whole process takes less than 5 minutes.</p>
        </div>

        <div className="steps-timeline">
          <div className="step-item">
            <div className="step-icon">
              <span className="step-number">1</span>
              <div className="step-inner-icon"></div>
            </div>
            <h4>Sign up</h4>
            <p>Create your free employer account in under 2 minutes</p>
          </div>
          <div className="step-divider"></div>
          
          <div className="step-item">
            <div className="step-icon">
              <span className="step-number">2</span>
              <div className="step-inner-icon"></div>
            </div>
            <h4>Post a job</h4>
            <p>Fill in role details and select your tier</p>
          </div>
          <div className="step-divider"></div>
          
          <div className="step-item">
            <div className="step-icon">
              <span className="step-number">3</span>
              <div className="step-inner-icon"></div>
            </div>
            <h4>Get verified</h4>
            <p>Verify your work email and your job goes live instantly</p>
          </div>
          <div className="step-divider"></div>
          
          <div className="step-item">
            <div className="step-icon">
              <span className="step-number">4</span>
              <div className="step-inner-icon"></div>
            </div>
            <h4>Track applications</h4>
            <p>Review AI-scored applicants in your hiring dashboard</p>
          </div>
        </div>

        <div className="steps-cta">
          <CustomButton
            text="Post a Job"
            backgroundColor="#fff"
            color="#000"
            borderColor="#3A6ED4"
            additionalClass="outline-cta-btn"
            handleClick={handlePostAJobClick}
          />
          <p className="steps-disclaimer">Free to sign up. Verified listings go live in minutes.</p>
        </div>
      </section>

      {/* 5. Footer CTA Section */}
      <section className="preview-footer-cta">
        <h2>Ready to find your next great hire?</h2>
        <p>Join employers already using Workbrook to hire faster, reach further, and hire right.</p>
        <CustomButton
          text="Post a Job Now"
          backgroundColor="#3A6ED4"
          color="#fff"
          borderColor="transparent"
          additionalClass="final-cta-btn"
          handleClick={handlePostAJobClick}
        />
        <p className="final-disclaimer">Free to sign up • Verified listings go live in minutes • No credit card required</p>
      </section>

      {/* 6. Footer Section */}
      <footer className="preview-footer">
        <div className="footer-container">
          <div className="footer-logo">workbrook</div>
          <div className="footer-links">
            <a href="https://workbrook.com/workbrook-privacy-policy/" target="_blank" rel="noreferrer">Privacy Policy</a>
            <a href="https://workbrook.com/apps-term-of-use/" target="_blank" rel="noreferrer">Terms of Use</a>
            <a href="mailto:support@workbrook.com">support@workbrook.com</a>
          </div>
        </div>
      </footer>

      {/* 7. Lead Capture Modal */}
      {showLeadModal && (
        <div className="lead-modal-overlay">
          <div className="lead-modal">
            <button className="lead-modal-close" onClick={() => setShowLeadModal(false)}>×</button>
            <div className="lead-modal-content">
              <h3>Join our community of HR leaders</h3>
              <p>Sign up to receive tips and resources to help you hire faster and better.</p>
              
              {leadSuccess ? (
                <div className="lead-success-msg">
                  Thanks for joining! We'll be in touch soon.
                </div>
              ) : (
                <form onSubmit={handleLeadSubmit} className="lead-modal-form">
                  <div className="input-group">
                    <input
                      type="email"
                      placeholder="you@company.com"
                      value={leadEmail}
                      onChange={(e) => setLeadEmail(e.target.value)}
                      required
                    />
                  </div>
                  {leadEmailError && <div className="error-text">{leadEmailError}</div>}
                  <CustomButton
                    text={leadLoading ? "Submitting..." : "Subscribe"}
                    backgroundColor="#3A6ED4"
                    color="#fff"
                    borderColor="transparent"
                    additionalClass="lead-submit-btn"
                    handleClick={handleLeadSubmit}
                    disabled={leadLoading}
                  />
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostAJobPreview;
